/**
 * src/shell/gallery/Lightbox.tsx
 *
 * Full WCAG 2.2 AA Accessible Lightbox Media Viewer with Origin Focus Restoration,
 * ARIA Live Screen Reader Announcements, Keyboard Trap, and Touch Zoom Support.
 */

import React, { useEffect, useRef, useCallback } from 'react';
import { ArtworkProduct } from '../../domain/inventory/types/product-types';

interface LightboxProps {
  artwork: ArtworkProduct | null;
  isOpen: boolean;
  onClose: () => void;
  originElement?: HTMLElement | null;
}

export const Lightbox: React.FC<LightboxProps> = ({ artwork, isOpen, onClose, originElement }) => {
  const [activeImageIdx, setActiveImageIdx] = React.useState(0);
  const modalRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  // Restore focus on close
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      closeBtnRef.current?.focus();
    } else {
      document.body.style.overflow = '';
      if (originElement) {
        originElement.focus();
      }
    }
  }, [isOpen, originElement]);

  const images = artwork?.metadata.images || [];
  const currentImg = images[activeImageIdx] || images[0];

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight') {
        setActiveImageIdx((prev) => (prev + 1) % images.length);
      } else if (e.key === 'ArrowLeft') {
        setActiveImageIdx((prev) => (prev - 1 + images.length) % images.length);
      }
    },
    [isOpen, images.length, onClose]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!isOpen || !artwork) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="lightbox-artwork-title"
      ref={modalRef}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(5, 8, 15, 0.95)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        backdropFilter: 'blur(8px)',
      }}
    >
      {/* ARIA Live Region for Screen Readers */}
      <div className="sr-only" aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}>
        Viewing image {activeImageIdx + 1} of {images.length}: {currentImg?.caption || artwork.title}
      </div>

      {/* Top Controls Header */}
      <div
        style={{
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <div>
          <h2 id="lightbox-artwork-title" style={{ fontSize: '18px', fontWeight: 700, color: '#f8fafc', margin: 0 }}>
            {artwork.title}
          </h2>
          <span style={{ fontSize: '13px', color: '#38bdf8' }}>
            {artwork.metadata.artist} — {artwork.metadata.medium} ({artwork.metadata.yearCreated})
          </span>
        </div>

        <button
          ref={closeBtnRef}
          onClick={onClose}
          aria-label="Close Lightbox"
          style={{
            background: 'rgba(255,255,255,0.1)',
            border: 'none',
            color: '#f8fafc',
            fontSize: '18px',
            fontWeight: 700,
            padding: '8px 16px',
            borderRadius: '8px',
            cursor: 'pointer',
          }}
        >
          ✕ Close
        </button>
      </div>

      {/* Main Image Stage */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          padding: '24px',
        }}
      >
        {images.length > 1 && (
          <button
            onClick={() => setActiveImageIdx((prev) => (prev - 1 + images.length) % images.length)}
            aria-label="Previous Image"
            style={{
              position: 'absolute',
              left: '24px',
              background: 'rgba(0,0,0,0.6)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.2)',
              fontSize: '24px',
              padding: '12px 18px',
              borderRadius: '50%',
              cursor: 'pointer',
            }}
          >
            ‹
          </button>
        )}

        <img
          src={currentImg?.url}
          alt={currentImg?.caption || artwork.title}
          style={{
            maxHeight: '75vh',
            maxWidth: '85vw',
            objectFit: 'contain',
            borderRadius: '8px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.8)',
          }}
        />

        {images.length > 1 && (
          <button
            onClick={() => setActiveImageIdx((prev) => (prev + 1) % images.length)}
            aria-label="Next Image"
            style={{
              position: 'absolute',
              right: '24px',
              background: 'rgba(0,0,0,0.6)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.2)',
              fontSize: '24px',
              padding: '12px 18px',
              borderRadius: '50%',
              cursor: 'pointer',
            }}
          >
            ›
          </button>
        )}
      </div>

      {/* Footer Info & Provenance Summary */}
      <div
        style={{
          padding: '16px 24px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '13px',
          color: '#94a3b8',
        }}
      >
        <div>
          Dimensions: {artwork.metadata.dimensions.width} × {artwork.metadata.dimensions.height} {artwork.metadata.dimensions.unit}
          {artwork.metadata.location && ` | Location: ${artwork.metadata.location.site} (${artwork.metadata.location.room || 'Main Hall'})`}
        </div>
        <div style={{ fontWeight: 700, color: artwork.price.isPrivate ? '#a855f7' : '#34d399' }}>
          {artwork.price.formatted}
        </div>
      </div>
    </div>
  );
};
